import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Truck,
    HardHat,
    Users2,
    Boxes,
    Briefcase,
    CalendarDays,
    Wrench,
    BadgeCheck,
    DollarSign,
    Receipt,
    Fuel,
    UserCog,
    ScrollText,
    KeyRound,
    ShieldAlert,
    BookOpen,
    Settings,
    LogOut,
    Menu,
    X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { BrandLockup } from "../components/Brand";
import dict from "../i18n/es-MX";

// nav items: each is { key, path, icon, adminOnly?, soon? }
const NAV_ITEMS = [
    { key: "panel", path: "/panel", icon: LayoutDashboard },
    { key: "assets", path: "/activos", icon: Truck },
    { key: "operators", path: "/operadores", icon: HardHat },
    { key: "clients", path: "/clientes", icon: Users2 },
    { key: "materials", path: "/materiales", icon: Boxes },
    { key: "jobs", path: "/trabajos", icon: Briefcase },
    { key: "calendar", path: "/calendario", icon: CalendarDays },
    { key: "maintenance", path: "/mantenimiento", icon: Wrench },
    { key: "verification", path: "/verificacion", icon: BadgeCheck },
    { key: "finance", path: "/finanzas", icon: DollarSign },
    { key: "expenses", path: "/gastos", icon: Boxes },
    { key: "tolls", path: "/casetas", icon: Receipt },
    { key: "fuel", path: "/combustible", icon: Fuel },
    { key: "users", path: "/usuarios", icon: UserCog, adminOnly: true },
    { key: "settings", path: "/configuracion/checklists", icon: Settings, adminOnly: true },
    { key: "audit", path: "/auditoria", icon: ScrollText, adminOnly: true },
    { key: "resetRequests", path: "/superadmin/reset-requests", icon: KeyRound, superadminOnly: true },
    { key: "wipeDb", path: "/superadmin/wipe", icon: ShieldAlert, superadminOnly: true },
    { key: "manuals", path: "/manuales", icon: BookOpen, superadminOnly: true },
];

function NavItem({ item, isAdmin, isSuperadmin, onClick }) {
    const { key, path, icon: Icon, soon, adminOnly, superadminOnly } = item;
    const label = dict.nav[key];
    if (superadminOnly && !isSuperadmin) return null;
    const blocked = adminOnly && !isAdmin;

    if (blocked) {
        return (
            <div
                data-testid={`nav-${key}-blocked`}
                title={dict.auth.notAllowed}
                className="group flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 cursor-not-allowed select-none"
            >
                <Icon size={18} className="text-slate-700" />
                <span className="text-sm">{label}</span>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-700 border border-slate-800 rounded px-1.5 py-0.5">
                    ●
                </span>
            </div>
        );
    }

    return (
        <NavLink
            to={path}
            onClick={onClick}
            data-testid={`nav-${key}`}
            className={({ isActive }) =>
                [
                    "group flex items-center gap-3 px-3 py-2.5 rounded-lg transition",
                    isActive
                        ? "bg-brand-orange/15 text-brand-orange-soft border-l-2 border-brand-orange"
                        : "text-slate-300 hover:bg-slate-800/70 hover:text-slate-50 border-l-2 border-transparent",
                ].join(" ")
            }
        >
            <Icon
                size={18}
                className="opacity-90 group-[.active]:text-brand-orange"
            />
            <span className="text-sm flex-1">{label}</span>
            {soon && (
                <span
                    className="text-[10px] uppercase tracking-wider text-slate-500 group-hover:text-slate-400"
                    data-testid={`nav-${key}-soon`}
                >
                    {dict.common.comingSoon}
                </span>
            )}
        </NavLink>
    );
}

export default function DesktopShell() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const isAdmin = user?.role === "admin" || user?.role === "superadmin";
    const isSuperadmin = user?.role === "superadmin";

    const handleLogout = () => {
        logout();
        navigate("/login", { replace: true });
    };

    const sidebar = (
        <div className="flex flex-col h-full">
            <div className="px-5 pt-5 pb-4 border-b border-slate-800">
                <BrandLockup size={36} />
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" data-testid="desktop-sidebar-nav">
                {NAV_ITEMS.map((item) => (
                    <NavItem
                        key={item.key}
                        item={item}
                        isAdmin={isAdmin}
                        isSuperadmin={isSuperadmin}
                        onClick={() => setDrawerOpen(false)}
                    />
                ))}
            </nav>
            <div className="px-3 py-4 border-t border-slate-800">
                <div className="px-2 pb-3">
                    <div className="text-xs text-slate-500 uppercase tracking-wider">
                        {dict.roles[user?.role] || user?.role}
                    </div>
                    <div className="text-sm font-medium text-slate-100 truncate" data-testid="sidebar-user-name">
                        {user?.full_name || user?.name}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                </div>
                <button
                    type="button"
                    onClick={handleLogout}
                    data-testid="logout-button"
                    className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-md border border-slate-700 hover:border-brand-orange hover:bg-slate-800 text-sm transition"
                >
                    <LogOut size={16} />
                    {dict.common.signOut}
                </button>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-950 text-slate-50">
            {/* Top hazard stripe */}
            <div
                className="fixed top-0 left-0 right-0 h-1 z-30"
                style={{
                    background:
                        "repeating-linear-gradient(135deg, #F97316 0 18px, #0f172a 18px 36px)",
                }}
            />

            {/* Desktop sidebar */}
            <aside className="hidden md:flex fixed top-1 left-0 bottom-0 w-64 bg-slate-900/90 backdrop-blur border-r border-slate-800 z-20 flex-col">
                {sidebar}
            </aside>

            {/* Mobile drawer (for admin viewing on phone) */}
            {drawerOpen && (
                <div className="fixed inset-0 z-40 md:hidden">
                    <div
                        className="absolute inset-0 bg-black/60"
                        onClick={() => setDrawerOpen(false)}
                    />
                    <aside className="absolute top-0 left-0 bottom-0 w-72 bg-slate-900 border-r border-slate-800 flex flex-col">
                        <div className="flex items-center justify-end p-3">
                            <button
                                onClick={() => setDrawerOpen(false)}
                                data-testid="close-drawer-button"
                                className="text-slate-400 hover:text-slate-100 p-2"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        {sidebar}
                    </aside>
                </div>
            )}

            {/* Top mobile bar */}
            <header className="md:hidden sticky top-1 z-10 h-14 bg-slate-900/90 backdrop-blur border-b border-slate-800 px-3 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    data-testid="open-drawer-button"
                    className="text-slate-300 hover:text-slate-100 p-2"
                >
                    <Menu size={22} />
                </button>
                <BrandLockup size={32} />
                <button
                    type="button"
                    onClick={handleLogout}
                    data-testid="logout-button-mobile"
                    className="text-slate-300 hover:text-slate-100 p-2"
                >
                    <LogOut size={20} />
                </button>
            </header>

            {/* Content */}
            <main className="md:ml-64 pt-1 min-h-screen" data-testid="desktop-main">
                <div className="px-5 md:px-8 py-6 md:py-8 max-w-6xl mx-auto">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
